import { resolveClientErrorMessage } from '../lib/apiErrorMessages';
import { getRememberLoginPreference } from '../utils/rememberLogin';

/**
 * Security: only `REACT_APP_*` variables from `.env` are inlined into the browser bundle at build time.
 * Use them for non-secret public config (e.g. API base URL). Never put API keys or tokens in `REACT_APP_*`.
 * Session auth uses HttpOnly cookies (`credentials: 'include'`) — no JWT in localStorage or Authorization headers.
 *
 * In development, never call the default API origin (loopback :3001) directly from the browser.
 * That bypasses the CRA dev proxy → "Failed to fetch" (CORS) and skips setupProxy long timeouts.
 * Always use same-origin `/api` instead when the env URL is just local :3001.
 */
function shouldForceRelativeApiInDev(envUrl) {
  if (process.env.NODE_ENV !== 'development' || !envUrl) return false;
  try {
    const u = new URL(envUrl);
    const h = u.hostname.toLowerCase();
    const port = u.port || (u.protocol === 'https:' ? '443' : '80');
    const loopback =
      h === 'localhost' ||
      h === '127.0.0.1' ||
      h === '[::1]' ||
      h === '::1';
    return loopback && String(port) === '3001';
  } catch {
    return /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\]):3001$/i.test(envUrl);
  }
}

function resolveApiBase() {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
  if (shouldForceRelativeApiInDev(envUrl)) {
    return '/api';
  }
  if (envUrl) {
    return `${envUrl}/api`;
  }
  return '/api';
}

const API_BASE = resolveApiBase();

const fetchCredentials = 'include';

/** Parse JSON without throwing SyntaxError (whitespace-only bodies count as empty). */
function tryParseJson(text) {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

/** Parse JSON for successful API bodies; throws a clear Error instead of SyntaxError. */
function parseJsonBody(text, context) {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch (cause) {
    const detail = cause && cause.message ? cause.message : 'parse error';
    throw new Error(context ? `Invalid JSON in ${context}: ${detail}` : `Invalid JSON response: ${detail}`);
  }
}

/** When the API rejects the session, leave the app shell — auth is enforced on the server, not by React state. */
function redirectToLoginIfUnauthorizedInApp(status, apiPath) {
  if (typeof window === 'undefined' || status !== 401) return;
  if (apiPath && apiPath.startsWith('/auth/refresh')) return;
  if (!window.location.pathname.startsWith('/app')) return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

/** Single-flight refresh so parallel 401s do not rotate the refresh token twice. */
let refreshInFlight = null;

async function postSessionRefresh() {
  if (refreshInFlight) return refreshInFlight;
  const url = `${API_BASE}/auth/refresh`;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: fetchCredentials,
        headers: { 'Content-Type': 'application/json' },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

const AUTH_PATHS_NO_REFRESH = /^\/auth\/(login|signup|refresh|invite\/validate)/;

function shouldAttemptSessionRefresh(status, apiPath, retryAfterRefresh) {
  return (
    status === 401 &&
    !retryAfterRefresh &&
    apiPath &&
    !AUTH_PATHS_NO_REFRESH.test(apiPath)
  );
}

/**
 * fetch wrapper: on 401, silently refresh session once and retry (JSON, multipart, blob downloads).
 */
async function fetchWithSessionRetry(url, init = {}, opts = {}) {
  const apiPath = opts.apiPath || '';
  const retryAfterRefresh = Boolean(opts._retryAfterRefresh);
  let res;
  try {
    res = await fetch(url, { credentials: fetchCredentials, ...init });
  } catch (networkError) {
    if (opts.throwOnNetworkError === false) throw networkError;
    const sameOrigin = !String(url).startsWith('http');
    const hint = sameOrigin
      ? `This app calls ${url} (same origin). In dev, CRA proxies /api to http://127.0.0.1:3001 — start the API with npm run dev from the repo root. If REACT_APP_API_URL is http://localhost:3001, remove it in dev (direct calls often fail with "Failed to fetch").`
      : `This app calls ${url}. Check REACT_APP_API_URL, CORS, and that the API is reachable.`;
    const cause = networkError && networkError.message ? ` ${networkError.message}` : '';
    throw new Error(
      `Network error: could not complete request.${cause} Check your internet connection. ${hint}`
    );
  }

  if (shouldAttemptSessionRefresh(res.status, apiPath, retryAfterRefresh)) {
    let refreshed = await postSessionRefresh();
    if (!refreshed && getRememberLoginPreference()) {
      await new Promise((r) => setTimeout(r, 200));
      refreshed = await postSessionRefresh();
    }
    if (refreshed) {
      return fetchWithSessionRetry(url, init, { ...opts, _retryAfterRefresh: true });
    }
    redirectToLoginIfUnauthorizedInApp(res.status, apiPath);
  }

  return res;
}

async function request(method, path, body, opts = { _retryAfterRefresh: false }) {
  const headers = { 'Content-Type': 'application/json' };

  const url = `${API_BASE}${path}`;
  let res;
  try {
    res = await fetchWithSessionRetry(
      url,
      {
        method,
        headers,
        body: body != null ? JSON.stringify(body) : undefined,
      },
      { apiPath: path, _retryAfterRefresh: opts._retryAfterRefresh }
    );
  } catch (networkError) {
    throw networkError;
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    let err = { error: res.statusText };
    const parsed = tryParseJson(errText);
    if (parsed && typeof parsed === 'object') {
      err = parsed;
    } else if (errText && errText.trim()) {
      err = { error: errText.trim().slice(0, 200) };
    }
    if (
      res.status === 401 &&
      !opts._retryAfterRefresh &&
      path &&
      !AUTH_PATHS_NO_REFRESH.test(path)
    ) {
      redirectToLoginIfUnauthorizedInApp(res.status, path);
    }
    const message = resolveClientErrorMessage(path, err, errText);
    // Include path for 404s to help debug "Not found"
    const detail = res.status === 404 && err.path ? ` (${err.path})` : '';
    const apiErr = new Error(message + detail);
    if (err && err.code) apiErr.code = err.code;
    throw apiErr;
  }

  if (res.status === 204) return null;
  const text = await res.text();
  return parseJsonBody(text, `${method} ${path}`);
}

// --- Auth API ---
export const authAPI = {
  login: (email, password, inviteToken, rememberMe = false) =>
    request('POST', '/auth/login', {
      email: String(email || '').trim().toLowerCase(),
      password,
      rememberMe: !!rememberMe,
      ...(inviteToken ? { inviteToken: String(inviteToken).trim() } : {}),
    }),
  signup: (name, email, password, inviteToken) =>
    request('POST', '/auth/signup', { name, email, password, inviteToken }),
  validateInvite: (token) =>
    request('GET', `/auth/invite/validate?token=${encodeURIComponent(token)}`),
  /** Rotates refresh token + mints new access cookie; no JSON body. */
  refresh: () => request('POST', '/auth/refresh', undefined, { _retryAfterRefresh: true }),
  logout: () => request('POST', '/auth/logout'),
  /** Invalidates all refresh sessions for the current user (sign out everywhere). */
  logoutAllDevices: () => request('POST', '/auth/logout-all'),
  getProfile: () => request('GET', '/auth/me'),
  updateProfile: (data) => request('PUT', '/auth/me', data),
  acceptInvite: (token) => request('POST', '/auth/accept-invite', { token }),
};

export const settingsAPI = {
  get: () => request('GET', '/settings'),
  updateNotifications: (prefs) => request('PUT', '/settings/notifications', prefs),
  getTeam: () => request('GET', '/settings/team'),
  createTeamInvite: (payload) => request('POST', '/settings/team/invites', payload),
  revokeTeamInvite: (membershipId) => request('DELETE', `/settings/team/invites/${membershipId}`),
  deleteAccount: (payload) => request('DELETE', '/settings/account', payload),
};

async function uploadResumeForParse(file) {
  const formData = new FormData();
  formData.append('resume', file);

  const url = `${API_BASE}/employees/parse-resume`;
  const apiPath = '/employees/parse-resume';
  const res = await fetchWithSessionRetry(url, { method: 'POST', body: formData }, { apiPath });

  const text = await res.text();
  if (!res.ok) {
    const err = tryParseJson(text) || { error: text.trim().slice(0, 200) || res.statusText };
    if (res.status === 401) redirectToLoginIfUnauthorizedInApp(res.status, apiPath);
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  return parseJsonBody(text, 'POST /employees/parse-resume');
}

/**
 * XMLHttpRequest with silent session refresh retry on 401 (multipart uploads).
 */
function xhrRequestWithSessionRetry(config) {
  const {
    method,
    url,
    apiPath,
    body,
    onUploadProgress,
    parseResponse = (xhr) => JSON.parse(xhr.responseText || '{}'),
  } = config;

  return new Promise((resolve, reject) => {
    let retried = false;

    const send = () => {
      const xhr = new XMLHttpRequest();
      if (typeof onUploadProgress === 'function') {
        xhr.upload.onprogress = onUploadProgress;
      }
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onload = async () => {
        if (
          xhr.status === 401 &&
          !retried &&
          shouldAttemptSessionRefresh(xhr.status, apiPath, false)
        ) {
          const refreshed = await postSessionRefresh();
          if (refreshed) {
            retried = true;
            send();
            return;
          }
          redirectToLoginIfUnauthorizedInApp(xhr.status, apiPath);
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(parseResponse(xhr));
          } catch {
            reject(new Error('Invalid server response'));
          }
          return;
        }

        let bodyOut = {};
        try {
          bodyOut = JSON.parse(xhr.responseText || '{}');
        } catch {
          bodyOut = { error: xhr.responseText?.trim()?.slice(0, 200) || xhr.statusText };
        }
        if (xhr.status === 401) redirectToLoginIfUnauthorizedInApp(xhr.status, apiPath);
        reject(new Error(bodyOut.error || `Request failed: ${xhr.status}`));
      };
      xhr.open(method, url);
      xhr.withCredentials = true;
      xhr.send(body);
    };

    send();
  });
}

/**
 * AI resume screening: multipart upload + structured extraction. Uses XHR for progress.
 * Upload bytes map to ~0–40% (XHR upload events often complete instantly on fast networks).
 * After the body is sent, progress eases toward ~94% until the response returns (server extraction).
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 */
function screenResumeExtract(file, onProgress) {
  const apiPath = '/employees/resume-screening/extract';
  const url = `${API_BASE}${apiPath}`;
  const fd = new FormData();
  fd.append('file', file);

  return new Promise((resolve, reject) => {
    let processingTimer = null;
    let lastProgress = 0;
    let retried = false;

    const setProg = (n) => {
      const next = Math.max(lastProgress, Math.min(100, Math.round(n)));
      lastProgress = next;
      if (typeof onProgress === 'function') onProgress(next);
    };

    const clearProcessingTimer = () => {
      if (processingTimer != null) {
        clearInterval(processingTimer);
        processingTimer = null;
      }
    };

    const startProcessingRamp = () => {
      clearProcessingTimer();
      setProg(Math.max(lastProgress, 40));
      processingTimer = window.setInterval(() => {
        const cap = 94;
        if (lastProgress >= cap) return;
        const room = cap - lastProgress;
        const delta = Math.max(0.35, room * 0.055);
        setProg(lastProgress + delta);
      }, 220);
    };

    const fail = (err) => {
      clearProcessingTimer();
      reject(err);
    };

    const send = () => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('loadstart', () => {
        if (lastProgress < 3) setProg(3);
      });

      xhr.upload.onprogress = (e) => {
        let uploadRatio = 0;
        if (e.lengthComputable && e.total > 0) {
          uploadRatio = e.loaded / e.total;
        } else if (file.size > 0 && e.loaded > 0) {
          uploadRatio = Math.min(1, e.loaded / file.size);
        }
        setProg(uploadRatio * 40);
      };

      xhr.upload.addEventListener('load', () => {
        setProg(Math.max(lastProgress, 40));
        startProcessingRamp();
      });

      xhr.onerror = () => fail(new Error('Network error during upload'));
      xhr.onload = async () => {
        clearProcessingTimer();
        if (
          xhr.status === 401 &&
          !retried &&
          shouldAttemptSessionRefresh(xhr.status, apiPath, false)
        ) {
          const refreshed = await postSessionRefresh();
          if (refreshed) {
            retried = true;
            send();
            return;
          }
          redirectToLoginIfUnauthorizedInApp(xhr.status, apiPath);
        }
        let body = {};
        try {
          body = JSON.parse(xhr.responseText || '{}');
        } catch {
          fail(new Error('Invalid server response'));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          setProg(100);
          resolve(body);
        } else {
          if (xhr.status === 401) redirectToLoginIfUnauthorizedInApp(xhr.status, apiPath);
          fail(new Error(body.error || `Request failed: ${xhr.status}`));
        }
      };

      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.send(fd);
    };

    send();
  });
}

/**
 * Bulk upload employees from a CSV or JSON file. Returns the import summary
 * including per-row status, errors, and counts. Surfaces real upload progress
 * via XHR (no synthetic ramp).
 *
 * @param {File} file
 * @param {{ dryRun?: boolean, onProgress?: (pct: number) => void }} [options]
 */
function importEmployeesBulk(file, options = {}) {
  const dryRun = Boolean(options.dryRun);
  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
  const apiPath = '/employees/import';
  const url = `${API_BASE}${apiPath}`;
  const fd = new FormData();
  fd.append('file', file);
  fd.append('dryRun', dryRun ? 'true' : 'false');

  return xhrRequestWithSessionRetry({
    method: 'POST',
    url,
    apiPath,
    body: fd,
    onUploadProgress: (e) => {
      if (!onProgress) return;
      if (e.lengthComputable && e.total > 0) {
        const pct = Math.max(0, Math.min(95, Math.round((e.loaded / e.total) * 95)));
        onProgress(pct);
      }
    },
    parseResponse: (xhr) => {
      if (onProgress) onProgress(100);
      return xhr.responseText ? JSON.parse(xhr.responseText) : null;
    },
  });
}

/** Normalize list endpoints that return either a bare array (legacy) or { employees, total, ... }. */
export function unwrapEmployeeListResponse(body) {
  if (Array.isArray(body)) return { employees: body, total: body.length, limit: body.length, offset: 0 };
  if (body && Array.isArray(body.employees)) {
    return {
      employees: body.employees,
      total: typeof body.total === 'number' ? body.total : body.employees.length,
      limit: body.limit,
      offset: body.offset,
      meta: body.meta,
    };
  }
  return { employees: [], total: 0, limit: 0, offset: 0 };
}

function buildEmployeesQuery(params = {}) {
  const q = new URLSearchParams();
  q.set('view', params.view || 'list');
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  if (params.q) q.set('q', params.q);
  if (params.department && params.department !== 'All') q.set('department', params.department);
  if (params.riskBand && params.riskBand !== 'all') q.set('riskBand', params.riskBand);
  if (params.includeMeta) q.set('includeMeta', '1');
  return q.toString();
}

// --- Employees API ---
export const employeesAPI = {
  list: (params = {}) =>
    request('GET', `/employees?${buildEmployeesQuery(params)}`).then(unwrapEmployeeListResponse),
  /** Fetches all pages (slim list view) — use only when the UI needs the full roster. */
  getAll: async (params = {}) => {
    const pageSize = params.limit || 500;
    let offset = 0;
    let total = Infinity;
    const all = [];
    let meta;
    while (offset < total) {
      const page = await employeesAPI.list({ ...params, limit: pageSize, offset });
      all.push(...page.employees);
      total = page.total;
      meta = page.meta || meta;
      offset += page.employees.length;
      if (!page.employees.length) break;
    }
    return { employees: all, total: all.length, meta };
  },
  getById: (id) => request('GET', `/employees/${id}`),
  /** Persisted turnover payload (no on-request scoring). */
  getTurnoverRisk: (id) => request('GET', `/employees/${id}/turnover-risk`),
  /** Canonical risk state — primary driver, contributors, classification (single source of truth). */
  getRiskState: (id) => request('GET', `/employees/${id}/risk-state`),
  /** Batch canonical risk states for multiple employees. */
  getRiskStates: (employeeIds) =>
    request('POST', '/employees/risk-state', { employeeIds }),
  /** OEWS + benchmark-derived comp panel for an employee (cached row; use refresh for live rebuild). */
  getCompBenchmark: (id) => request('GET', `/employees/${id}/comp-benchmark`),
  /** Persist fresh market intelligence for one employee (does not change risk scores). */
  refreshCompBenchmark: (id) => request('POST', `/employees/${id}/comp-benchmark/refresh`),
  create: (data) => request('POST', '/employees', data),
  /** Save employee after AI resume review (includes resumeProfile + resumeSource). */
  confirmResumeScreening: (data) => request('POST', '/employees/resume-screening/confirm', data),
  update: (id, data) => request('PUT', `/employees/${id}`, data),
  delete: (id, data) => request('DELETE', `/employees/${id}`, data),
  departureSurveyStep: (id, body) => request('POST', `/employees/${id}/departure-survey/step`, body),
  importBulk: importEmployeesBulk,
  getImportSummary: () => request('GET', '/employees/import/summary'),
  /** Admin: recompute the local 47-factor engine for the company (all or specific ids). */
  refreshRisk: (payload) => request('POST', '/employees/refresh-risk', payload || { all: true }),
  uploadResumeForParse,
  screenResumeExtract,
};

// --- Risks API ---
export const risksAPI = {
  getAll: () => request('GET', '/risks'),
  /** Local engine: distribution, top factors, patterns, teams, coverage, employees with turnover payloads */
  getAnalytics: () => request('GET', '/risks/analytics'),
};

/** Analytics dashboard, report summaries — Stavyn engine + explicit unavailable flags */
export const insightsAPI = {
  getAnalyticsDashboard: () => request('GET', '/risks/insights/analytics'),
  getReports: () => request('GET', '/risks/insights/reports'),
  getTechnicalReport: () => request('GET', '/risks/insights/technical-report'),
};

export async function downloadExportCsv(kind) {
  const grantBody = await request('POST', '/exports/download-grant', { kind });
  if (!grantBody || !grantBody.grant || !grantBody.downloadPath) {
    throw new Error('Invalid download grant response');
  }
  const headers = {
    'Content-Type': 'application/json',
    'X-Stavyn-Client': '1',
    'X-Requested-With': 'XMLHttpRequest',
  };
  const apiPath = `/exports/csv/${kind}`;
  const url = `${API_BASE}/${grantBody.downloadPath}`;
  let res;
  try {
    res = await fetchWithSessionRetry(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ grant: grantBody.grant }),
      },
      { apiPath }
    );
  } catch (e) {
    throw new Error(e.message || 'Download failed');
  }
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    const err = tryParseJson(errText) || { error: errText.trim().slice(0, 200) || res.statusText };
    if (res.status === 401) redirectToLoginIfUnauthorizedInApp(res.status, apiPath);
    throw new Error(err.error || `Export failed: ${res.status}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  const match = cd && cd.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : `${kind.replace(/\//g, '-')}.csv`;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

/** Presigned S3-compatible URLs; server returns 503 when STAVYN_STORAGE_* is not configured. */
export const storageAPI = {
  signedGet: (key, expiresIn) => {
    if (expiresIn == null || expiresIn === '') {
      return Promise.reject(new Error('expiresIn is required (seconds, within your role cap)'));
    }
    const q = new URLSearchParams({ key, expiresIn: String(expiresIn) });
    return request('GET', `/storage/signed-get?${q.toString()}`);
  },
  signedPut: (key, contentType, expiresIn) => {
    if (expiresIn == null || expiresIn === '') {
      return Promise.reject(new Error('expiresIn is required (seconds, within your role cap)'));
    }
    const q = new URLSearchParams({ key, expiresIn: String(expiresIn) });
    if (contentType) q.set('contentType', contentType);
    return request('GET', `/storage/signed-put?${q.toString()}`);
  },
};

// --- AI API (server-scheduled / internal use; no manual triggers in product UI) ---
export const aiAPI = {
  analyzeEmployee: (employeeId) => request('POST', `/ai/analyze/${employeeId}`),
  analyzeAll: () => request('POST', '/ai/analyze-all'),
  generatePlaybook: (employeeId, options = {}) =>
    request('POST', `/ai/playbook/${employeeId}`, options.refresh ? { refresh: true } : undefined),
};

/** Retention recommendation application tracking (SQLite-backed). */
export const recommendationApplicationAPI = {
  list: (employeeId) => {
    const q = new URLSearchParams({ employeeId: String(employeeId) });
    return request('GET', `/recommendations/application-status?${q.toString()}`);
  },
  updateStatus: (recommendationId, body) =>
    request('PATCH', `/recommendations/${encodeURIComponent(recommendationId)}/application-status`, body),
};

// --- Action Plans API ---
export const actionPlansAPI = {
  getAll: (params = {}) => {
    const q = new URLSearchParams();
    if (params.employeeId != null) q.set('employeeId', String(params.employeeId));
    if (params.sync) q.set('sync', '1');
    const qs = q.toString();
    return request('GET', qs ? `/action-plans?${qs}` : '/action-plans');
  },
  create: (data) => request('POST', '/action-plans', data),
  update: (id, data) => request('PUT', `/action-plans/${id}`, data),
  delete: (id) => request('DELETE', `/action-plans/${id}`),
};

// --- Demo booking API (requires session JWT) ---
export const demoAPI = {
  getSlots: (date) => request('GET', `/demo/slots?date=${encodeURIComponent(date)}`),
  book: (payload) => request('POST', '/demo/book', payload),
};

// --- Waiting list (public; no sign-in required) ---
export const waitingListAPI = {
  join: (payload) => request('POST', '/waiting-list', payload),
};

// --- Alerts API ---
export const alertsAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return request('GET', `/alerts${qs ? `?${qs}` : ''}`);
  },
  summary: () => request('GET', '/alerts/summary'),
  generate: () => request('POST', '/alerts/generate'),
  updateStatus: (id, status) => request('PATCH', `/alerts/${id}/status`, { status }),
};

// --- Risk history API ---
export const riskHistoryAPI = {
  getEmployee: (employeeId) => request('GET', `/risk-history/employees/${employeeId}`),
  getCompany: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.sinceIso) q.set('sinceIso', params.sinceIso);
    const qs = q.toString();
    return request('GET', qs ? `/risk-history/company?${qs}` : '/risk-history/company');
  },
  triggerSnapshot: () => request('POST', '/risk-history/snapshot'),
};

// --- Feature catalog ---
export const featureCatalogAPI = {
  list: () => request('GET', '/feature-catalog'),
  get: (key) => request('GET', `/feature-catalog/${encodeURIComponent(key)}`),
};

// --- Model quality (backtest, calibration, drift, bias) ---
export const modelQualityAPI = {
  summary: () => request('GET', '/model-quality/summary'),
};
